import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import { createBooking, updateBooking, getBooking } from '../api/bookingApi';
import { formatDateTime, formatTime, formatDisplayDateWithDay } from '../utils/dateHelpers';
import { getLocationById } from '../utils/locations';
import { decryptBookingId } from '../utils/crypto';
import { isStaffUnlocked } from '../utils/staffUnlock';
import './ContactInfoPage.css';

function ContactInfoPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const bookingId = searchParams.get('bookingId');
    const locationId = searchParams.get('location');
    const datetime = searchParams.get('datetime');
    const date = searchParams.get('date');
    const time = searchParams.get('time');
    const providerId = searchParams.get('providerId');
    const providerEmail = searchParams.get('providerEmail');
    const watsSlotId = searchParams.get('watsSlotId');
    const slotStart = searchParams.get('slotStart');
    const slotEnd = searchParams.get('slotEnd');
    const isRescheduling = searchParams.get('reschedule') === 'true';

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [emailWasProvided, setEmailWasProvided] = useState(false);

    const locationDetails = getLocationById(locationId);
    const locationDisplayName = locationDetails ? locationDetails.name : locationId;

    // M1: Update page title
    useEffect(() => {
        document.title = 'Complete Booking — The IMA Group';
    }, []);

    // Check if link is already expired on mount (e.g. if user refreshes after booking)
    useEffect(() => {
        const checkStatus = async () => {
            try {
                const response = await getBooking(bookingId);
                if (response && response.is_expired && !isStaffUnlocked(bookingId)) {
                    navigate(`/booking?bookingId=${bookingId}`);
                }
            } catch (err) {
                console.error('Failed to check booking status on refresh', err);
            }
        };
        if (bookingId) {
            checkStatus();
        }
    }, [bookingId, navigate]);

    const handleSubmit = async (formData) => {
        setSubmitting(true);
        setError('');

        try {
            const bookingFields = formData;

            const bookingData = {
                // Pass bookingId (decrypted if legacy, or token for backend to decrypt)
                bookingId: decryptBookingId(bookingId) || bookingId,
                location: locationDetails ? locationDetails.name : locationId, // Send proper Name to backend
                appointment_datetime: datetime,
                provider_id: providerId,
                provider_email: providerEmail,
                wats_slot_id: watsSlotId,
                slot_start: slotStart,
                slot_end: slotEnd,
                ...bookingFields
            };

            const response = isRescheduling
                ? await updateBooking(bookingId, bookingData)
                : await createBooking(bookingData);

            if (response.success) {
                setEmailWasProvided(Boolean(formData.email && formData.email.trim()));
                setSuccess(true);
                // Removed auto-redirect because the manage page is now immediately expired upon booking

            } else {
                setError(response.error || `Failed to ${isRescheduling ? 'update' : 'create'} booking`);
            }
        } catch (err) {
            if (err.response && err.response.data) {
                setError(err.response.data.error || 'Failed to create booking');
            } else {
                setError('An error occurred. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleGoBack = () => {
        navigate(`/book?bookingId=${bookingId}`);
    };

    if (success) {
        return (
            <div className="contact-info-page">
                <div className="success-message" role="status">
                    {/* M4: aria-hidden on decorative icon; heading conveys the success state */}
                    <div className="success-icon" aria-hidden="true">{'✓'}</div>
                    <h2>Booking Confirmed!</h2>
                    <p>Your appointment has been successfully scheduled.</p>
                    {emailWasProvided && (
                        <p>A confirmation email has been sent to your email address.</p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="contact-info-page">
            <div className="page-header">
                <h1>Complete Your Booking</h1>
                <p>Enter your contact information</p>
            </div>

            {/* L1: Semantic description list for appointment summary */}
            <div className="appointment-summary">
                <h3>Selected Appointment</h3>
                <dl className="summary-details">
                    <div className="summary-item">
                        <dt className="label">Location:</dt>
                        <dd className="value-container">
                            <span className="value">{locationDisplayName}</span>
                            {locationDetails && (
                                <div className="location-address-small" style={{ fontSize: '0.9em', color: '#333', marginTop: '4px' }}>
                                    {locationDetails.address.line1}, {locationDetails.address.city}, {locationDetails.address.state}
                                </div>
                            )}
                        </dd>
                    </div>
                    <div className="summary-item">
                        <dt className="label">Date:</dt>
                        <dd className="value">{formatDisplayDateWithDay(date)}</dd>
                    </div>
                    <div className="summary-item">
                        <dt className="label">Time:</dt>
                        <dd className="value">{formatTime(time)}</dd>
                    </div>
                </dl>
                <button className="back-button" onClick={handleGoBack}>
                    {'←'} Change Appointment
                </button>
            </div>

            {error && (
                <div className="error-banner" role="alert">
                    {error}
                </div>
            )}

            <ContactForm
                onSubmit={handleSubmit}
                submitButtonText={submitting ? 'Booking...' : 'Make Booking'}
            />
        </div>
    );
}

export default ContactInfoPage;
